if (!customElements.get('finch-product-buybox')) {
  customElements.define(
    'finch-product-buybox',
    class FinchProductBuybox extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.section = this.closest('product-info') || this.closest('section') || document;
        this.variantInput = this.querySelector('[data-fpb-variant-id]');
        this.quantityInput = this.querySelector('[data-fpb-quantity-input]');
        this.addButton = this.querySelector('[data-fpb-add]');
        this.addButtonText = this.querySelector('[data-fpb-add-text]');
        this.buyNowButton = this.querySelector('[data-fpb-buy-now]');
        this.price = this.querySelector('[data-fpb-price]');
        this.comparePrice = this.querySelector('[data-fpb-compare-price]');
        this.discountBadge = this.querySelector('[data-fpb-discount-badge]');
        this.inventory = this.querySelector('[data-fpb-inventory]');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.product-form__error-message');

        try {
          this.variants = JSON.parse(this.querySelector('[data-fpb-variants]')?.textContent || '[]');
        } catch (error) {
          this.variants = [];
          console.error('Product variant data could not be read.', error);
        }

        this.querySelectorAll('[data-fpb-option-input]').forEach((input) => {
          input.addEventListener('change', () => this.handleOptionChange(input));
        });

        this.querySelector('[data-fpb-quantity-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
        this.querySelector('[data-fpb-quantity-plus]')?.addEventListener('click', () => this.changeQuantity(1));
        this.quantityInput?.addEventListener('change', () => this.normalizeQuantity());
        this.buyNowButton?.addEventListener('click', () => this.buyNow());

        this.querySelectorAll('[data-fpb-dismiss-offer]').forEach((button) => {
          button.addEventListener('click', () => button.closest('.fpb__offer')?.remove());
        });

        const initialVariant = this.variants.find((variant) => String(variant.id) === this.variantInput?.value);
        if (initialVariant) this.applyVariant(initialVariant, false);
      }

      getSelectedOptions() {
        return Array.from(this.querySelectorAll('[data-fpb-option-input]'))
          .sort((first, second) => Number(first.dataset.optionIndex) - Number(second.dataset.optionIndex))
          .map((input) => input.value);
      }

      handleOptionChange(input) {
        const selectedOptions = this.getSelectedOptions();
        const variant = this.variants.find(
          (candidate) =>
            candidate.options.length === selectedOptions.length &&
            candidate.options.every((option, index) => option === selectedOptions[index])
        );

        if (variant) {
          this.applyVariant(variant, true);
        } else {
          this.applyUnavailableState();
        }
      }

      applyVariant(variant, updateHistory) {
        this.currentVariant = variant;

        if (this.variantInput) {
          this.variantInput.value = variant.id;
          this.variantInput.disabled = false;
        }

        const priceValue = Number(variant.priceValue);
        const compareAtPriceValue = Number(variant.compareAtPriceValue);
        const showComparePrice =
          Number.isFinite(priceValue) &&
          Number.isFinite(compareAtPriceValue) &&
          compareAtPriceValue > priceValue;
        const discountPercentage = showComparePrice
          ? Math.round(((compareAtPriceValue - priceValue) / compareAtPriceValue) * 100)
          : 0;

        if (this.price) this.price.textContent = variant.price;
        if (this.comparePrice) {
          this.comparePrice.hidden = !showComparePrice;
          this.comparePrice.textContent = showComparePrice ? variant.compareAtPrice : '';
        }
        if (this.discountBadge) {
          const showDiscountBadge = showComparePrice && discountPercentage > 0;
          this.discountBadge.hidden = !showDiscountBadge;
          this.discountBadge.textContent = showDiscountBadge ? `${discountPercentage}%` : '';
          if (showDiscountBadge) {
            this.discountBadge.setAttribute('aria-label', `${discountPercentage}% korting`);
          } else {
            this.discountBadge.removeAttribute('aria-label');
          }
        }

        const available = Boolean(variant.available);
        this.setPurchaseAvailability(available, available ? 'add' : 'soldOut');
        this.updateInventory(variant);
        this.updateQuantityRules(variant);
        this.showVariantMedia(variant.featuredMediaId);
        this.clearError();

        if (updateHistory) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }

        this.dispatchEvent(
          new CustomEvent('finch:variant-change', {
            bubbles: true,
            detail: { variant },
          })
        );
      }

      applyUnavailableState() {
        this.currentVariant = null;
        if (this.variantInput) this.variantInput.disabled = true;
        if (this.comparePrice) this.comparePrice.hidden = true;
        if (this.discountBadge) this.discountBadge.hidden = true;
        this.setPurchaseAvailability(false, 'unavailable');
        if (this.inventory) this.inventory.hidden = true;
      }

      setPurchaseAvailability(available, labelType) {
        if (this.addButton) {
          this.addButton.disabled = !available;
          const label =
            labelType === 'soldOut'
              ? this.addButton.dataset.soldOutLabel
              : labelType === 'unavailable'
                ? this.addButton.dataset.unavailableLabel
                : this.addButton.dataset.addLabel;
          if (this.addButtonText && label) this.addButtonText.textContent = label;
        }

        if (this.buyNowButton) this.buyNowButton.disabled = !available;
      }

      updateInventory(variant) {
        if (!this.inventory) return;

        const usesManualInventory = this.inventory.dataset.inventorySource === 'manual';
        const quantity = usesManualInventory
          ? Math.max(0, Number(this.inventory.dataset.manualQuantity) || 0)
          : Math.max(0, Number(variant.inventoryQuantity) || 0);
        const threshold = Number(this.inventory.dataset.threshold) || 0;
        const tracked = usesManualInventory || (variant.inventoryManagement === 'shopify' && variant.inventoryPolicy !== 'continue');
        const modeAllowsDisplay = this.inventory.dataset.displayMode === 'always' || quantity <= threshold;
        const visible = tracked && variant.available && quantity > 0 && modeAllowsDisplay;

        this.inventory.hidden = !visible;
        if (!visible) return;

        const quantityNode = this.inventory.querySelector('[data-fpb-inventory-quantity]');
        const progress = this.inventory.querySelector('[data-fpb-inventory-progress]');
        const maximum = Math.max(1, Number(this.inventory.dataset.progressMax) || 1);

        if (quantityNode) quantityNode.textContent = quantity;
        if (progress) {
          progress.max = maximum;
          progress.value = Math.min(quantity, maximum);
        }
      }

      updateQuantityRules(variant) {
        if (!this.quantityInput) return;

        const minimum = Math.max(1, Number(variant.quantityMin) || 1);
        const increment = Math.max(1, Number(variant.quantityIncrement) || 1);
        const maximum = variant.quantityMax == null ? null : Number(variant.quantityMax);

        this.quantityInput.min = minimum;
        this.quantityInput.step = increment;
        if (maximum && Number.isFinite(maximum)) {
          this.quantityInput.max = maximum;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.normalizeQuantity();
      }

      changeQuantity(direction) {
        if (!this.quantityInput) return;
        const increment = Math.max(1, Number(this.quantityInput.step) || 1);
        this.quantityInput.value = Number(this.quantityInput.value || this.quantityInput.min || 1) + direction * increment;
        this.normalizeQuantity();
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      normalizeQuantity() {
        if (!this.quantityInput) return;

        const minimum = Math.max(1, Number(this.quantityInput.min) || 1);
        const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Number.POSITIVE_INFINITY;
        const increment = Math.max(1, Number(this.quantityInput.step) || 1);
        let value = Number(this.quantityInput.value);

        if (!Number.isFinite(value)) value = minimum;
        value = Math.max(minimum, Math.min(maximum, value));
        value = minimum + Math.round((value - minimum) / increment) * increment;
        this.quantityInput.value = Math.max(minimum, Math.min(maximum, value));
      }

      showVariantMedia(mediaId) {
        if (!mediaId) return;
        const thumbnail = this.section.querySelector(`[data-fp-thumb][data-media-id="${mediaId}"]`);
        thumbnail?.click();
      }

      async buyNow() {
        if (!this.buyNowButton || !this.currentVariant || !this.currentVariant.available) return;

        const requiredConfigurator = this.querySelector('[data-fic][data-required="true"]');
        if (requiredConfigurator && requiredConfigurator.dataset.ready !== 'true') {
          requiredConfigurator.dispatchEvent(
            new CustomEvent('finch:configurator-required', {
              bubbles: true,
              detail: { source: 'buy-now' },
            })
          );

          const dialog = requiredConfigurator.querySelector('[data-fic-dialog]');
          if (!dialog?.open) requiredConfigurator.querySelector('[data-fic-action="open"]')?.click();
          return;
        }

        const productForm = this.variantInput?.form || this.querySelector('form[data-type="add-to-cart-form"]');
        if (!productForm) {
          this.showError(this.buyNowButton.dataset.errorLabel);
          return;
        }

        const label = this.buyNowButton.querySelector('[data-fpb-buy-now-text]');
        const originalLabel = this.buyNowButton.dataset.defaultLabel || label?.textContent;
        this.buyNowButton.disabled = true;
        if (label) label.textContent = this.buyNowButton.dataset.processingLabel || originalLabel;
        this.clearError();

        const formData = new FormData(productForm);
        formData.set('id', this.currentVariant.id);
        formData.set('quantity', this.quantityInput?.value || 1);

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || this.buyNowButton.dataset.errorLabel);
          }
          window.location.assign('/checkout');
        } catch (error) {
          this.showError(error.message);
          this.buyNowButton.disabled = false;
          if (label) label.textContent = originalLabel;
        }
      }

      showError(message) {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorMessage.textContent = message;
        this.errorWrapper.hidden = false;
      }

      clearError() {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorMessage.textContent = '';
        this.errorWrapper.hidden = true;
      }
    }
  );
}

if (!customElements.get('finch-delivery-estimate')) {
  customElements.define(
    'finch-delivery-estimate',
    class FinchDeliveryEstimate extends HTMLElement {
      connectedCallback() {
        const dateContainer = this.querySelector('[data-fpb-delivery-dates]');
        if (!dateContainer || typeof Intl?.DateTimeFormat !== 'function') return;

        const minimumDays = Math.max(1, Number(this.dataset.minBusinessDays) || 4);
        const maximumDays = Math.max(minimumDays, Number(this.dataset.maxBusinessDays) || 7);
        const earliestDate = this.addBusinessDays(new Date(), minimumDays);
        const latestDate = this.addBusinessDays(new Date(), maximumDays);
        const locale = document.documentElement.lang || undefined;
        const formatter = new Intl.DateTimeFormat(locale, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          ...(earliestDate.getFullYear() !== latestDate.getFullYear() ? { year: 'numeric' } : {}),
        });

        const earliestTime = document.createElement('time');
        earliestTime.dateTime = this.toDateString(earliestDate);
        earliestTime.textContent = formatter.format(earliestDate);

        const separator = document.createElement('span');
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = '–';

        const latestTime = document.createElement('time');
        latestTime.dateTime = this.toDateString(latestDate);
        latestTime.textContent = formatter.format(latestDate);

        dateContainer.replaceChildren(earliestTime, separator, latestTime);
      }

      addBusinessDays(date, days) {
        const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        let remaining = days;

        while (remaining > 0) {
          result.setDate(result.getDate() + 1);
          const weekday = result.getDay();
          if (weekday !== 0 && weekday !== 6) remaining -= 1;
        }

        return result;
      }

      toDateString(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }
  );
}
